import {
  CONTENT_FORMAT,
  blocksFromElements,
  isContentElement,
  isTextElement,
  normalizeBlocks,
  trimBlocks,
} from "./consentContent";
import {
  addElement,
  ELEMENT_DEFS,
  nextElementId,
  setElementText,
} from "./consentElements";
import { isPredefinedType } from "./consentTemplateModel";

/** Drawn as the common clinic header / patient information band. */
export const HEADER_ELEMENT_TYPES = [
  "clinicLogo",
  "clinicInfo",
  "image",
  "patientInfo",
  "appointmentInfo",
  "doctorInfo",
];

export const HEADER_FIELDS = [
  "clinic.name",
  "clinic.address",
  "clinic.phone",
  "clinic.email",
  "clinic.timings",
  "doctor.name",
  "doctor.qualification",
  "doctor.speciality",
  "doctor.department",
  "doctor.designation",
  "doctor.experience",
  "doctor.registrationNo",
];

/** Drawn as the common declaration / signature band. */
export const FOOTER_ELEMENT_TYPES = [
  "consentDeclaration",
  "signatureBlock",
  "signature",
];

export const SHOW_DECLARATION_AND_SIGNATURES = false;

const asArray = (value) => (Array.isArray(value) ? value : []);

/** Every element of a design, in print order, pages flattened. */
export const designElements = (design) =>
  asArray(design?.pages).flatMap((page) => asArray(page?.elements));

export const splitDesign = (design) => {
  const elements = designElements(design);

  let start = 0;
  let titleTaken = false;
  while (start < elements.length) {
    const { type } = elements[start];

    if (HEADER_ELEMENT_TYPES.includes(type)) {
      start += 1;
      continue;
    }
    if (type === "heading" && !titleTaken) {
      titleTaken = true;
      start += 1;
      continue;
    }
    break;
  }

  let end = elements.length;
  while (end > start && FOOTER_ELEMENT_TYPES.includes(elements[end - 1].type)) {
    end -= 1;
  }

  return {
    header: elements.slice(0, start),
    body: elements.slice(start, end),
    footer: elements.slice(end),
  };
};

/** Body elements the one editor does NOT own — kept exactly as they are. */
export const preservedBodyElements = (design) =>
  splitDesign(design).body.filter((element) => !isTextElement(element));

const PATIENT_ELEMENT_TYPES = ["patientInfo", "appointmentInfo"];

/** Does this design already draw the clinic's own details? */
export const hasClinicHeader = (design) =>
  designElements(design).some((element) => element.type === "clinicInfo");

export const ensureClinicHeader = (design) => {
  const elements = designElements(design);
  const existing = elements.find((element) => element.type === "clinicInfo");

  // Built through the normal reducer, so it is an ordinary element with an
  // ordinary id — there is nothing special about the header.
  const made = addElement(
    { pages: [{ pageNumber: 1, elements: [] }], settings: {} },
    { type: "clinicInfo" }
  ).element;
  const taken = new Set(elements.map((element) => element.id));
  const header =
    existing ||
    {
      ...made,
      id: taken.has(made.id) ? nextElementId(design, "clinicInfo") : made.id,
    };

  const carried = new Set(header.props?.fields || []);
  const redundant = (element, inLeadingRun) => {
    if (!inLeadingRun) return false;
    if (element.type === "clinicLogo") return header.props?.showLogo !== false;
    if (element.type !== "doctorInfo") return false;

    const fields = element.props?.fields || [];
    return fields.length > 0 && fields.every((field) => carried.has(field));
  };

  const kept = [];
  let leading = true;
  let logoHeight = 0;

  for (const element of elements) {
    if (leading && !HEADER_ELEMENT_TYPES.includes(element.type)) leading = false;

    if (element === existing) {
      kept.push(element);
      continue;
    }
    if (redundant(element, leading)) {
      if (element.type === "clinicLogo" && !logoHeight) {
        logoHeight = Number(element.props?.height) || 0;
      }
      continue;
    }
    kept.push(element);
  }

  if (existing && kept.length === elements.length) return design;

  const withLogo = (element) =>
    logoHeight
      ? { ...element, props: { ...element.props, logoHeight } }
      : element;

  const placed = existing
    ? kept.map((element) => (element === existing ? withLogo(element) : element))
    : [withLogo(header), ...kept];

  return {
    ...(design && typeof design === "object" ? design : {}),
    pages: [
      {
        ...(asArray(design?.pages)[0] || {}),
        pageNumber: 1,
        elements: placed,
      },
    ],
    settings: design?.settings && typeof design.settings === "object" ? design.settings : {},
  };
};

export const headerBands = (header) =>
  asArray(header).reduce((bands, element) => {
    const label = PATIENT_ELEMENT_TYPES.includes(element.type)
      ? "Patient information"
      : "Clinic header";
    const last = bands[bands.length - 1];

    if (last?.label === label) last.elements.push(element);
    else bands.push({ label, elements: [element] });

    return bands;
  }, []);

export const designAsShown = (design) => {
  if (SHOW_DECLARATION_AND_SIGNATURES) return design;

  const { footer } = splitDesign(design);
  if (!footer.length) return design;

  const hidden = new Set(footer.map((element) => element.id));

  return {
    ...(design && typeof design === "object" ? design : {}),
    pages: asArray(design?.pages).map((page) => ({
      ...page,
      elements: asArray(page?.elements).filter(
        (element) => !hidden.has(element.id)
      ),
    })),
  };
};


export const designForPreview = (design) => {
  const shown = designAsShown(design);
  let changed = false;
  const pages = asArray(shown?.pages).map((page) => {
    const elements = asArray(page?.elements);
    let boundary = 0;
    while (
      boundary < elements.length &&
      (HEADER_ELEMENT_TYPES.includes(elements[boundary].type) ||
        elements[boundary].type === "heading")
    ) boundary += 1;

    if (!boundary) return page;
    const leading = elements.slice(0, boundary);
    const category = (element) => {
      if (element.type === "heading") return 2;
      if (element.type === "patientInfo") return 1;
      if (element.type === "appointmentInfo") return 1.5;
      return 0;
    };
    const ordered = [...leading].sort((a, b) => category(a) - category(b));
    const patientIndex = ordered.findIndex((element) => element.type === "patientInfo");
    const appointmentIndex = ordered.findIndex((element) => element.type === "appointmentInfo");
    let previewLeading = ordered;

    if (patientIndex >= 0) {
      const patient = ordered[patientIndex];
      const appointment = ordered[appointmentIndex];
      const appointmentFields = appointment
        ? asArray(appointment.props?.fields)
        : ELEMENT_DEFS.appointmentInfo.make().props.fields;
      previewLeading = ordered
        .filter((_, index) => index !== appointmentIndex)
        .map((element) =>
          element === patient
            ? {
                ...patient,
                props: {
                  ...(patient.props || {}),
                  previewAppointmentFields: appointmentFields,
                },
              }
            : element
        );
    }

    const sameOrder = previewLeading.length === leading.length &&
      previewLeading.every((element, index) => element === leading[index]);
    if (sameOrder) return page;
    changed = true;
    return { ...page, elements: [...previewLeading, ...elements.slice(boundary)] };
  });

  return changed ? { ...shown, pages } : shown;
};

export const bodyLayout = (design) => {
  const rows = [];
  let placed = false;

  for (const element of splitDesign(design).body) {
    if (isTextElement(element)) {
      if (!placed) {
        rows.push({ kind: "content" });
        placed = true;
      }
      continue;
    }
    rows.push({ kind: "element", element });
  }
  if (!placed) rows.push({ kind: "content" });

  return rows;
};

/** The content the editor opens with, converted from whatever is stored. */
export const contentBlocksOf = (design, lang = "en") =>
  blocksFromElements(splitDesign(design).body, lang);

export const hasLegacyContent = (design) =>
  splitDesign(design).body.some(
    (element) => isTextElement(element) && !isContentElement(element)
  );

const contentElementId = (design) => {
  const existing = splitDesign(design).body.find(isContentElement);
  return existing?.id || nextElementId(design, "richText");
};

const contentElement = (design, blocks) => {
  const existing = splitDesign(design).body.find(isContentElement);

  return {
    id: contentElementId(design),
    type: "richText",
    content: { en: "", hi: "" },
    style: { ...(existing?.style || {}) },
    layout: { ...(existing?.layout || { pageBreakBefore: false, pageBreakAfter: false }) },
    props: {
      ...(existing?.props || {}),
      format: CONTENT_FORMAT,
      blocks: trimBlocks(blocks),
    },
  };
};

export const applyContentBlocks = (design, blocks) => {
  const { header, body, footer } = splitDesign(design);
  const element = contentElement(design, blocks);

  const nextBody = [];
  let placed = false;

  for (const item of body) {
    if (isTextElement(item)) {
      if (!placed) {
        nextBody.push(element);
        placed = true;
      }
      continue;
    }
    nextBody.push(item);
  }
  if (!placed) nextBody.push(element);

  return {
    ...(design && typeof design === "object" ? design : {}),
    pages: [
      {
        ...(asArray(design?.pages)[0] || {}),
        pageNumber: 1,
        elements: [...header, ...nextBody, ...footer],
      },
    ],
    settings: design?.settings && typeof design.settings === "object" ? design.settings : {},
  };
};

export const simpleStarterDesign = ({ name, displayName, hindiName } = {}) => {
  const heading = (displayName || name || "CONSENT FORM").toUpperCase();

  let design = { pages: [{ pageNumber: 1, elements: [] }], settings: {} };
  for (const type of [
    // One letterhead element: it draws the logo, the clinic and the doctor.
    "clinicInfo",
    "heading",
    "patientInfo",
    "consentDeclaration",
    "signatureBlock",
  ]) {
    design = addElement(design, { type }).design;
  }

  design = setElementText(design, "heading-1", "content", "en", heading);
  if (hindiName) {
    design = setElementText(design, "heading-1", "content", "hi", hindiName);
  }

  return applyContentBlocks(design, []);
};

export const hasPrintableContent = (design) => {
  const { body } = splitDesign(design);
  if (!body.length) return false;

  const blocks = normalizeBlocks(contentBlocksOf(design));
  if (blocks.some((block) => block.runs.some((run) => run.text.trim()))) return true;

  // A form whose body is mapped fields (an old design) is still printable.
  return body.some((element) => !isTextElement(element));
};

// ----------------------------------------------------------------- the form

export const ALL_SPECIALITIES_LABEL = "All Specialities";

export const validateConsentDetails = ({
  name,
  type,
  speciality = [],
  allSpecialities = false,
} = {}) => {
  const errors = {};

  if (!String(name || "").trim()) errors.name = "Consent name is required";
  if (!String(type || "").trim()) errors.type = "Consent format is required";

  if (allSpecialities && asArray(speciality).length > 0) {
    errors.speciality = `"${ALL_SPECIALITIES_LABEL}" cannot be combined with specific specialities`;
  } else if (!allSpecialities && asArray(speciality).length === 0) {
    errors.speciality = `Select at least one speciality, or choose "${ALL_SPECIALITIES_LABEL}"`;
  }

  return errors;
};

const specialityId = (item) => (typeof item === "string" ? item : item?._id);

export const consentDetailsPayload = ({
  name,
  type,
  typeLabel = "",
  speciality = [],
  allSpecialities = false,
} = {}) => ({
  name: String(name || "").trim(),
  type: String(type || "").trim(),
  typeLabel: String(typeLabel || "").trim(),
  speciality: allSpecialities
    ? []
    : asArray(speciality).map(specialityId).filter(Boolean),
  allSpecialities: Boolean(allSpecialities),
});

/** A stored row → the form's own state, against the speciality master. */
export const consentDetailsForm = (template = {}, options = []) => {
  const ids = asArray(template.speciality).map(specialityId).filter(Boolean);
  const byId = new Map(asArray(options).map((item) => [String(item._id), item]));

  const type = template.type || "";

  return {
    name: template.name || "",
    type,

    typeLabel: isPredefinedType(type) ? "" : template.typeLabel || "",
    speciality: ids.map((id) => byId.get(String(id)) || { _id: id, name: "" }),
    allSpecialities: ids.length === 0,
  };
};

/** The list column: the speciality names, or "All Specialities" for `[]`. */
export const specialityNames = (template = {}, options = []) => {
  const ids = asArray(template.speciality).map(specialityId).filter(Boolean);
  if (!ids.length) return [];

  const byId = new Map(asArray(options).map((item) => [String(item._id), item]));
  return ids.map((id) => byId.get(String(id))?.name || "—");
};
